<template>
  <div class="w-full bg-slate-50 min-h-screen p-4" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-5xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-slate-700">Marcas de Veículos</h1>
        <p class="text-sm text-slate-500">Gerenciamento dos fabricantes da frota</p>
      </div>

      <!-- FORMULÁRIO DE CADASTRO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <div class="flex items-center gap-3">
                  <v-icon color="orange" small>mdi-plus-box-outline</v-icon>
                  <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                      {{ brandData.brand_id ? 'EDITAR MARCA' : 'NOVA MARCA' }}
                  </h2>
              </div>
              <div v-if="brandData.brand_id" class="px-3 py-1 bg-white/10 rounded-full border border-white/20">
                  <span class="text-[9px] text-orange-400 font-bold uppercase tracking-widest">
                      Editando registro #{{ brandData.brand_id }}
                  </span>
              </div>
          </div>
          
          <div class="p-8">
              <v-form @submit.prevent="createOrUpdate" class="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                  <div class="md:col-span-8">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Nome da Marca</label>
                      <input 
                        v-model="brandData.brand_desc"
                        ref="brandName"
                        type="text"
                        placeholder="Ex: Toyota, Ford, etc."
                        class="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase"
                      >
                  </div>
                  <div class="md:col-span-4">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Código Interno</label>
                      <input 
                        v-model="brandData.brand_num"
                        ref="brandNum"
                        type="text"
                        placeholder="000"
                        class="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
                      >
                  </div>
                  
                  <div class="md:col-span-12 flex justify-end gap-3 mt-4">
                      <button 
                        v-if="brandData.brand_id"
                        @click="newData"
                        type="button"
                        class="text-slate-500 hover:text-slate-700 font-bold text-[10px] uppercase px-4 py-2 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button 
                        @click="createOrUpdate"
                        type="button"
                        class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[10px] px-10 py-2.5 rounded shadow hover:shadow-md transform hover:-translate-y-0.5 transition-all uppercase tracking-widest inline-flex items-center justify-center gap-2 w-auto max-w-max"
                      >
                        <v-icon small color="white" style="font-size: 12px;">{{ brandData.brand_id ? 'mdi-pencil' : 'mdi-check' }}</v-icon>
                        {{ brandData.brand_id ? 'EDITAR' : 'SALVAR' }}
                      </button>
                  </div>
              </v-form>
          </div>
      </section>

      <!-- LISTAGEM -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-4 py-4 flex items-center w-full" style="background-color: #333333;">
              <div class="flex items-center gap-2 flex-none">
                <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                <h2 class="text-white font-bold uppercase tracking-wider text-[11px]">MARCAS CADASTRADAS</h2>
              </div>
              
              <div class="flex items-center gap-4 flex-1 justify-end">
                <div class="relative w-80">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <v-icon color="white" small class="opacity-50">mdi-magnify</v-icon>
                  </div>
                  <input 
                    v-model="search"
                    placeholder="PESQUISAR..."
                    class="bg-white/10 border border-white/20 rounded-lg py-2 pl-10 pr-4 text-[11px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest"
                  >
                </div>
              </div>
          </div>

          <div class="overflow-x-auto">
             <v-data-table
              :headers="headers"
              :items="data"
              :search="search"
              :loading="loader"
              :items-per-page="10"
              class="custom-table"
            >
              <template v-slot:item.brand_desc="{ item }">
                <span class="text-sm font-bold text-slate-700 uppercase tracking-tight">{{ item.brand_desc }}</span>
              </template>

              <template v-slot:item.brand_num="{ item }">
                <span class="text-xs font-mono text-slate-400">{{ item.brand_num || '---' }}</span>
              </template>

              <template v-slot:item.action="{ item }">
                 <div class="flex justify-end gap-2">
                    <button 
                      @click="loadData(item)"
                      class="p-2 rounded-lg text-slate-400 hover:bg-orange-50 hover:text-orange-500 transition-colors"
                    >
                      <v-icon small color="currentColor">mdi-pencil</v-icon>
                    </button>
                    <button 
                      @click="deleteItem(item.brand_id)"
                      class="p-2 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                    >
                      <v-icon small color="currentColor">mdi-delete</v-icon>
                    </button>
                 </div>
              </template>
             </v-data-table>
          </div>
      </section>

    </div>

    <!-- Snackbar -->
    <v-snackbar
      multi-line
      :color="snack.color"
      v-model="snack.show"
      transition="fade-transition"
    >
      {{ snack.message }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data() {
    return {
      search: "",
      loader: true,
      showSearch: false,
      data: [],
      brandData: {
        brand_desc: "",
        brand_id: null,
        brand_num: "",
      },
      snack: {
        show: false,
        message: "",
        color: "",
      },
      headers: [
        {
          text: "Nome",
          align: "start",
          sortable: false,
          value: "brand_desc",
        },
        {
          text: "Código",
          align: "start",
          sortable: false,
          value: "brand_num",
        },
        { text: "Ações", align: "right", value: "action", sortable: false },
      ],
    };
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.data = [];
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/vehicles/brands`)
        .then((r) => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    newData() {
      this.brandData.brand_desc = "";
      this.brandData.brand_id = null;
      this.brandData.brand_num = "";
      this.$refs.brandName.focus();
    },
    loadData(data) {
      this.brandData.brand_desc = data.brand_desc;
      this.brandData.brand_id = data.brand_id;
      this.brandData.brand_num = data.brand_num;
    },
    createOrUpdate() {
      if (this.brandData.brand_id) {
        this.$http
          .put(`/vehicles/brands/${this.brandData.brand_id}`, this.brandData)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = true;
            this.initialize();
            this.$store.dispatch("setLoader", this.loader);
            this.newData();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$http
          .post(`/vehicles/brands`, this.brandData)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = true;
            this.initialize();
            this.$store.dispatch("setLoader", this.loader);
            this.newData();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/vehicles/brands/${id}`)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.initialize();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style scoped>
.top-bar {
  padding: 30px 15px;
}
.title {
  text-transform: uppercase;
  font-weight: 400;
}
.maxPage {
  width: 100%;
}
.search-field {
  margin-top: 10px;
}
.space {
  margin: 0 10px;
}
</style>
